import React, { useState, useEffect } from 'react';
import { Container } from './types';
import { fetchAvailableContainers } from '../../middleware/api/compute_job_apis';
import './compute-jobs.css';

interface ContainerSelectorProps {
  /** Callback when a container is selected */
  onChange: (container: Container | null) => void;

  /** Currently selected container */
  value?: Container | null;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Placeholder text */
  placeholder?: string;
}

const ContainerSelector: React.FC<ContainerSelectorProps> = ({
  onChange,
  value,
  disabled = false,
  placeholder = 'Select a container image',
}) => {
  const [containers, setContainers] = useState<Container[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch containers on component mount
  useEffect(() => {
    const loadContainers = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedContainers = await fetchAvailableContainers();
        setContainers(fetchedContainers);
      } catch (err) {
        console.error('Failed to load containers:', err);
        setError('Failed to load container images. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadContainers();
  }, []);

  // Handle selection change
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedImage = event.target.value;

    if (!selectedImage) {
      onChange(null);
      return;
    }

    const selectedContainer = containers.find(
      (container) => container.container_image === selectedImage,
    );

    onChange(selectedContainer || null);
  };

  // Show loading state
  if (loading) {
    return (
      <select className="listbox" disabled>
        <option>Loading containers...</option>
      </select>
    );
  }

  // Show error state
  if (error) {
    return (
      <div>
        <select className="listbox" disabled>
          <option>Error loading containers</option>
        </select>
        <div
          className="error-message"
          style={{ color: 'var(--bs-danger)', marginTop: '5px', fontSize: '14px' }}
        >
          {error}
        </div>
      </div>
    );
  }

  // Show empty state
  if (containers.length === 0) {
    return (
      <select className="listbox" disabled>
        <option>No containers available</option>
      </select>
    );
  }

  return (
    <select
      name="container-selector"
      className="listbox"
      value={value?.container_image || ''}
      onChange={handleChange}
      disabled={disabled}
      data-placeholder-shown={!value ? 'T' : 'F'}
      style={{ lineHeight: '100%' }}
    >
      <option hidden value="">
        {placeholder}
      </option>
      {containers.map((container, index) => (
        <option key={index} value={container.container_image}>
          {container.name}
        </option>
      ))}
    </select>
  );
};

export default ContainerSelector;
